"use client";

import { useState } from "react";
import { Download, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface ResourceUnlockFormProps {
  resourceSlug: string;
  resourceTitle: string;
}

type Status = "idle" | "submitting" | "success" | "error";

export function ResourceUnlockForm({ resourceSlug, resourceTitle }: ResourceUnlockFormProps) {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch("/api/resource-download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          resourceSlug,
          resourceTitle,
          fullName: formData.get("fullName"),
          email: formData.get("email"),
          company: formData.get("company") || "",
          country: formData.get("country") || "",
          phone: formData.get("phone") || "",
          role: formData.get("role") || "",
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Submission failed");
      }

      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-2xl border border-[var(--color-primary)]/40 bg-gradient-to-br from-[var(--color-primary)]/15 via-[var(--color-surface)] to-[var(--color-surface)] p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-primary)]/20">
          <CheckCircle className="h-7 w-7 text-[var(--color-primary)]" />
        </div>
        <h3 className="mb-2 text-xl font-bold">You're All Set!</h3>
        <p className="mb-6 text-sm text-[var(--color-muted)]">
          Thank you for your interest. You now have full access to this resource.
          We've also sent a copy to your email.
        </p>
        <p className="text-sm text-[var(--color-primary)]">
          Scroll down to continue reading the full content below.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-8">
      <div className="mb-6 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--color-primary)]/15">
          <Download className="h-6 w-6 text-[var(--color-primary)]" />
        </div>
        <h3 className="text-lg font-bold">Unlock the Full Resource</h3>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Enter your details to access all chapters and download the PDF version.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[var(--color-muted)]">Full Name *</label>
            <Input
              name="fullName"
              required
              placeholder="John Smith"
              disabled={status === "submitting"}
              className="bg-[var(--color-background)]"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[var(--color-muted)]">Email *</label>
            <Input
              name="email"
              type="email"
              required
              placeholder="john@company.com"
              disabled={status === "submitting"}
              className="bg-[var(--color-background)]"
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[var(--color-muted)]">Company</label>
            <Input
              name="company"
              placeholder="Your company name"
              disabled={status === "submitting"}
              className="bg-[var(--color-background)]"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[var(--color-muted)]">Country</label>
            <Input
              name="country"
              placeholder="UAE / Saudi Arabia / Thailand..."
              disabled={status === "submitting"}
              className="bg-[var(--color-background)]"
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[var(--color-muted)]">Phone / WhatsApp</label>
            <Input
              name="phone"
              placeholder="+971 ..."
              disabled={status === "submitting"}
              className="bg-[var(--color-background)]"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[var(--color-muted)]">Job Role</label>
            <Input
              name="role"
              placeholder="Owner / Manager / Technician..."
              disabled={status === "submitting"}
              className="bg-[var(--color-background)]"
            />
          </div>
        </div>

        {status === "error" && (
          <p className="text-sm text-red-400">{errorMsg}</p>
        )}

        <Button
          type="submit"
          disabled={status === "submitting"}
          className="w-full bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] font-semibold text-[var(--color-background)] shadow-lg shadow-[var(--color-glow-soft)]"
        >
          {status === "submitting" ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" />
              Unlock Full Access
            </>
          )}
        </Button>

        <p className="text-center text-xs text-[var(--color-muted)]">
          We respect your privacy. Your info will never be shared.
        </p>
      </form>
    </div>
  );
}
